// pages/admin/DeliveryDetails/DeliveryDetails.jsx
import React, { useState, useEffect, useRef } from "react";
import { useParams, useHistory } from "react-router-dom";
import { Button } from "primereact/button";
import { Card } from "primereact/card";
import { Badge } from "primereact/badge";
import { Timeline } from "primereact/timeline";
import { Skeleton } from "primereact/skeleton";
import { Dialog } from "primereact/dialog";
import { Accordion, AccordionTab } from "primereact/accordion";
import { GoogleMap, LoadScript, Marker, Polyline } from "@react-google-maps/api";
import axios from "axios";
import { toast } from "react-toastify";
import styles from "./DeliveryDetails.module.scss";
import { DEV } from "../../services/constants";

const DeliveryDetails = () => {
    const { id } = useParams();
    const history = useHistory();
    const mapRef = useRef(null);

    const deliveryId = id;

    const [loading, setLoading] = useState(true);
    const [deliveryData, setDeliveryData] = useState(null);
    const [selectedProof, setSelectedProof] = useState(null);
    const [showProofDialog, setShowProofDialog] = useState(false);

    // Location trail states
    const [locationTrail, setLocationTrail] = useState([]);
    const [loadingLocations, setLoadingLocations] = useState(false);
    const [mapCenter, setMapCenter] = useState({ lat: 33.006257, lng: 73.15074 });
    const [isMapLoaded, setIsMapLoaded] = useState(false);

    useEffect(() => {
        fetchDeliveryDetails();
        fetchLocationTrail();
    }, [deliveryId]);

    const fetchDeliveryDetails = async () => {
        setLoading(true);
        try {
            const response = await axios.get(`${DEV}/getDelivery/${deliveryId}`, {
                headers: {
                    Authorization: `Bearer ${localStorage.getItem("token")}`,
                },
            });

            if (response.data.success) {
                setDeliveryData(response.data.data);
            }
        } catch (error) {
            console.error("Error fetching delivery details:", error);
            toast.error("Failed to load delivery details");
        } finally {
            setLoading(false);
        }
    };

    const fetchLocationTrail = async () => {
        setLoadingLocations(true);
        try {
            const response = await axios.get(`${DEV}/delivery/${deliveryId}/locations`, {
                headers: {
                    Authorization: `Bearer ${localStorage.getItem("token")}`,
                },
            });

            if (response.data.success) {
                const locations = response.data.data.map((loc) => ({
                    lat: loc.latitude,
                    lng: loc.longitude,
                    accuracy: loc.accuracy,
                    timestamp: loc.timestamp,
                    driverName: loc.driverName,
                }));

                console.log(`✅ Loaded ${locations.length} location points`);
                setLocationTrail(locations);

                // Calculate center of all locations
                if (locations.length > 0) {
                    const center = calculateMapCenter(locations);
                    setMapCenter(center);
                    console.log("📍 Map centered at:", center);
                }
            }
        } catch (error) {
            console.error("❌ Error fetching location trail:", error);
            toast.error("Failed to load location trail");
        } finally {
            setLoadingLocations(false);
        }
    };

    // Calculate center point of all locations
    const calculateMapCenter = (locations) => {
        if (locations.length === 0) {
            return { lat: 33.006257, lng: 73.15074 };
        }

        const sum = locations.reduce(
            (acc, loc) => ({
                lat: acc.lat + loc.lat,
                lng: acc.lng + loc.lng,
            }),
            { lat: 0, lng: 0 },
        );

        return {
            lat: sum.lat / locations.length,
            lng: sum.lng / locations.length,
        };
    };

    // Fit map bounds to show all locations
    const fitMapBounds = () => {
        if (mapRef.current && locationTrail.length > 0) {
            const bounds = new window.google.maps.LatLngBounds();

            // Include all trail points
            locationTrail.forEach((location) => {
                bounds.extend(new window.google.maps.LatLng(location.lat, location.lng));
            });

            // Include delivery stops
            if (deliveryData?.stops) {
                deliveryData.stops.forEach((stop) => {
                    bounds.extend(new window.google.maps.LatLng(stop.latitude, stop.longitude));
                });
            }

            mapRef.current.fitBounds(bounds);
            console.log("🗺️ Map bounds fitted to all locations");
        }
    };

    const formatTime = (seconds) => {
        if (!seconds) return "0h 0m";
        const hours = Math.floor(seconds / 3600);
        const minutes = Math.floor((seconds % 3600) / 60);
        return `${hours}h ${minutes}m`;
    };

    const formatDate = (date) => {
        if (!date) return "N/A";
        return new Date(date).toLocaleString("en-US", {
            month: "short",
            day: "numeric",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
        });
    };

    const getEventIcon = (eventType) => {
        const iconMap = {
            DELIVERY_CREATED: "pi-file-plus",
            DELIVERY_ASSIGNED: "pi-user-plus",
            DELIVERY_STARTED: "pi-play",
            DELIVERY_PAUSED: "pi-pause",
            DELIVERY_RESUMED: "pi-play-circle",
            DELIVERY_COMPLETED: "pi-check-circle",
            DELIVERY_MARKED_INCOMPLETE: "pi-exclamation-triangle",
            STOP_COMPLETED: "pi-map-marker",
            STOP_INCOMPLETE: "pi-times-circle",
            ROUTE_OPTIMIZED: "pi-directions",
        };
        return iconMap[eventType] || "pi-circle";
    };

    const getEventColor = (eventType) => {
        const colorMap = {
            DELIVERY_CREATED: "#6366f1",
            DELIVERY_ASSIGNED: "#8b5cf6",
            DELIVERY_STARTED: "#10b981",
            DELIVERY_PAUSED: "#f59e0b",
            DELIVERY_RESUMED: "#10b981",
            DELIVERY_COMPLETED: "#059669",
            DELIVERY_MARKED_INCOMPLETE: "#ef4444",
            STOP_COMPLETED: "#10b981",
            STOP_INCOMPLETE: "#f97316",
            ROUTE_OPTIMIZED: "#3b82f6",
        };
        return colorMap[eventType] || "#64748b";
    };

    const getEventTitle = (event) => {
        const titleMap = {
            DELIVERY_CREATED: "Delivery Created",
            DELIVERY_ASSIGNED: "Assigned to Driver",
            DELIVERY_STARTED: "Delivery Started",
            DELIVERY_PAUSED: "Delivery Paused",
            DELIVERY_RESUMED: "Delivery Resumed",
            DELIVERY_COMPLETED: "Delivery Completed",
            DELIVERY_MARKED_INCOMPLETE: "Marked Incomplete",
            STOP_COMPLETED: `Stop #${event.data?.stopNumber} Completed`,
            STOP_INCOMPLETE: `Stop #${event.data?.stopNumber} Incomplete`,
            ROUTE_OPTIMIZED: "Route Optimized",
        };
        return titleMap[event.eventType] || event.eventType;
    };

    const getEventDescription = (event) => {
        switch (event.eventType) {
            case "DELIVERY_CREATED":
                return `Delivery created in system`;

            case "DELIVERY_ASSIGNED":
                return `Assigned to ${event.data?.driverName || "driver"}`;

            case "DELIVERY_STARTED":
                const startLocation = event.data?.startLocation ? `at (${event.data.startLocation.latitude?.toFixed(4)}, ${event.data.startLocation.longitude?.toFixed(4)})` : "at origin";
                return `Delivery started ${startLocation} with ${event.data?.totalStops || 0} stops`;

            case "DELIVERY_PAUSED":
                const estimatedDuration = event.data?.estimatedDuration ? ` (Est. ${event.data.estimatedDuration} min)` : "";
                return `${event.data?.reason || "Unknown reason"}${estimatedDuration}`;

            case "DELIVERY_RESUMED":
                return `Resumed after ${formatTime(event.data?.pauseDuration || 0)} pause`;

            case "DELIVERY_COMPLETED":
                const stats = event.data?.statistics;
                return `Successfully delivered ${stats?.deliveredStops || 0} of ${stats?.totalStops || 0} stops (${stats?.completionRate || 0}% completion)`;

            case "DELIVERY_MARKED_INCOMPLETE":
                const incStats = event.data?.statistics;
                return `Delivery incomplete: ${incStats?.deliveredStops || 0} delivered, ${incStats?.incompleteStops || 0} incomplete (${incStats?.completionRate || 0}% completion)`;

            case "STOP_COMPLETED":
                return `${event.data?.clientName || "Customer"} - ${event.data?.packagesDelivered || event.data?.totalPackages || 0} packages delivered`;

            case "STOP_INCOMPLETE":
                return `${event.data?.clientName || "Customer"} - ${event.data?.reason || "Unknown reason"}`;

            case "ROUTE_OPTIMIZED":
                return `Route optimized for ${event.data?.totalStops || 0} stops`;

            default:
                return "";
        }
    };

    const renderTimelineMetadata = (event) => {
        const metadata = [];

        if (event.data?.totalDuration) {
            metadata.push({
                icon: "pi-clock",
                label: "Duration",
                value: formatTime(event.data.totalDuration),
                color: "#6366f1",
            });
        }

        if (event.data?.activeDuration) {
            metadata.push({
                icon: "pi-play-circle",
                label: "Active Time",
                value: formatTime(event.data.activeDuration),
                color: "#10b981",
            });
        }

        if (event.data?.totalPauseDuration) {
            metadata.push({
                icon: "pi-pause",
                label: "Pause Time",
                value: formatTime(event.data.totalPauseDuration),
                color: "#f59e0b",
            });
        }

        if (event.data?.totalDistance) {
            metadata.push({
                icon: "pi-map",
                label: "Distance",
                value: `${event.data.totalDistance.toFixed(1)} km`,
                color: "#3b82f6",
            });
        }

        if (event.data?.numberOfPauses) {
            metadata.push({
                icon: "pi-pause-circle",
                label: "Pauses",
                value: event.data.numberOfPauses.toString(),
                color: "#f59e0b",
            });
        }

        if (event.data?.metrics) {
            const metrics = event.data.metrics;

            if (metrics.totalPackages) {
                metadata.push({
                    icon: "pi-box",
                    label: "Packages",
                    value: metrics.totalPackages.toString(),
                    color: "#8b5cf6",
                });
            }

            if (metrics.averageTimePerStop) {
                metadata.push({
                    icon: "pi-chart-bar",
                    label: "Avg Time/Stop",
                    value: formatTime(metrics.averageTimePerStop),
                    color: "#ec4899",
                });
            }
        }

        return metadata;
    };

    const renderTimelineContent = (item) => {
        const metadata = renderTimelineMetadata(item);

        return (
            <div className={styles.timelineContent} style={{ borderLeftColor: getEventColor(item.eventType) }}>
                <div className={styles.timelineHeader}>
                    <div className={styles.timelineTitle}>
                        <h4>{getEventTitle(item)}</h4>
                        <Badge value={item.eventType.replace(/_/g, " ")} className={styles.eventTypeBadge} style={{ backgroundColor: `${getEventColor(item.eventType)}20`, color: getEventColor(item.eventType) }} />
                    </div>
                    <span className={styles.timelineTime}>
                        <i className="pi pi-calendar" />
                        {formatDate(item.timestamp)}
                    </span>
                </div>

                {getEventDescription(item) && (
                    <p className={styles.timelineDescription}>
                        <i className="pi pi-info-circle" />
                        {getEventDescription(item)}
                    </p>
                )}

                {metadata.length > 0 && (
                    <div className={styles.timelineMetadata}>
                        {metadata.map((meta, index) => (
                            <div key={index} className={styles.metadataItem} style={{ borderColor: meta.color }}>
                                <i className={`pi ${meta.icon}`} style={{ color: meta.color }} />
                                <div className={styles.metadataContent}>
                                    <span className={styles.metadataLabel}>{meta.label}</span>
                                    <span className={styles.metadataValue}>{meta.value}</span>
                                </div>
                            </div>
                        ))}
                    </div>
                )}

                {item.data?.incompleteStops && item.data.incompleteStops.length > 0 && (
                    <div className={styles.incompleteStopsDetails}>
                        <h5>
                            <i className="pi pi-exclamation-triangle" />
                            Incomplete Stops ({item.data.incompleteStops.length})
                        </h5>
                        <div className={styles.incompleteStopsList}>
                            {item.data.incompleteStops.slice(0, 3).map((stop, index) => (
                                <div key={index} className={styles.incompleteStopItem}>
                                    <span className={styles.stopNum}>#{stop.stopNumber}</span>
                                    <span className={styles.stopClient}>{stop.clientName}</span>
                                    <span className={styles.stopReason}>{stop.reason}</span>
                                </div>
                            ))}
                            {item.data.incompleteStops.length > 3 && <span className={styles.moreStops}>+{item.data.incompleteStops.length - 3} more</span>}
                        </div>
                    </div>
                )}
            </div>
        );
    };

    const getStopStatusBadge = (status) => {
        const badgeMap = {
            "Not Delivered": { severity: "info", icon: "pi-clock" },
            Delivered: { severity: "success", icon: "pi-check" },
            Incomplete: { severity: "warning", icon: "pi-exclamation-triangle" },
        };
        return badgeMap[status] || { severity: "info", icon: "pi-circle" };
    };

    const getPackageStatusBadge = (status) => {
        const statusMap = {
            Delivered: { severity: "success", icon: "pi-check-circle", label: "Delivered" },
            Incomplete: { severity: "warning", icon: "pi-exclamation-circle", label: "Incomplete" },
            "Not Delivered": { severity: "info", icon: "pi-clock", label: "Not Delivered" },
        };
        return statusMap[status] || { severity: "secondary", icon: "pi-circle", label: status };
    };

    const getPackageSummary = () => {
        if (!deliveryData?.stops || deliveryData.stops.length === 0) {
            return { delivered: [], notDelivered: [] };
        }

        const delivered = [];
        const notDelivered = [];

        deliveryData.stops.forEach((stop) => {
            if (stop.packages && stop.packages.length > 0) {
                stop.packages.forEach((pkg) => {
                    const packageInfo = {
                        ...pkg,
                        stopNumber: stop.stopNumber,
                        stopName: stop.clientName,
                        stopStatus: stop.status,
                    };

                    if (pkg.stopPackageStatus === "Delivered") {
                        delivered.push(packageInfo);
                    } else {
                        notDelivered.push(packageInfo);
                    }
                });
            }
        });

        return { delivered, notDelivered };
    };

    const handleViewProof = (stop) => {
        setSelectedProof(stop);
        setShowProofDialog(true);
    };

    if (loading) {
        return (
            <div className={styles.deliveryDetails}>
                <div className={styles.pageHeader}>
                    <Skeleton width="200px" height="40px" />
                    <Skeleton width="100px" height="40px" />
                </div>
                <div className={styles.contentGrid}>
                    <Skeleton width="100%" height="400px" />
                    <Skeleton width="100%" height="400px" />
                </div>
            </div>
        );
    }

    if (!deliveryData) {
        return (
            <div className={styles.errorContainer}>
                <i className="pi pi-exclamation-triangle" style={{ fontSize: "3rem", color: "#ef4444" }} />
                <h3>Delivery Not Found</h3>
                <Button label="Go Back" icon="pi pi-arrow-left" onClick={() => history.goBack()} />
            </div>
        );
    }

    const { delivery, stops, statistics } = deliveryData;

    return (
        <div className={styles.deliveryDetails}>
            {/* Page Header */}
            <div className={styles.pageHeader}>
                <div className={styles.headerLeft}>
                    <Button icon="pi pi-arrow-left" rounded text className={styles.backButton} onClick={() => history.goBack()} />
                    <div>
                        <h1>Delivery Details</h1>
                        <p className={styles.deliveryId}>#{delivery.deliveryId}</p>
                    </div>
                </div>
                <div className={styles.headerRight}>
                    <Badge value={delivery.status} severity={delivery.status === "Completed" ? "success" : delivery.status === "In Transit" ? "info" : delivery.status === "Incomplete" ? "warning" : "secondary"} className={styles.statusBadge} />
                </div>
            </div>

            {/* Summary Cards */}
            <div className={styles.summaryCards}>
                <Card className={styles.summaryCard}>
                    <div className={styles.cardContent}>
                        <i className="pi pi-map-marker" />
                        <div>
                            <h3>{statistics?.totalStops || 0}</h3>
                            <p>Total Stops</p>
                        </div>
                    </div>
                </Card>
                <Card className={styles.summaryCard}>
                    <div className={styles.cardContent}>
                        <i className="pi pi-check-circle" />
                        <div>
                            <h3>{statistics?.completedStops || 0}</h3>
                            <p>Delivered</p>
                        </div>
                    </div>
                </Card>
                <Card className={styles.summaryCard}>
                    <div className={styles.cardContent}>
                        <i className="pi pi-exclamation-triangle" />
                        <div>
                            <h3>{statistics?.incompleteStops || 0}</h3>
                            <p>Incomplete</p>
                        </div>
                    </div>
                </Card>
                <Card className={styles.summaryCard}>
                    <div className={styles.cardContent}>
                        <i className="pi pi-clock" />
                        <div>
                            <h3>{statistics?.completionPercentage || 0}%</h3>
                            <p>Completion Rate</p>
                        </div>
                    </div>
                </Card>
            </div>

            {/* Main Content Grid */}
            <div className={styles.contentGrid}>
                {/* Timeline Section */}
                <Card className={styles.timelineSection}>
                    <h2 className={styles.sectionTitle}>
                        <i className="pi pi-clock" />
                        Delivery Timeline
                    </h2>

                    {delivery.timeline && delivery.timeline.length > 0 ? (
                        <Timeline
                            value={delivery.timeline}
                            align="left"
                            className={styles.customTimeline}
                            marker={(item) => (
                                <div className={styles.timelineMarker} style={{ backgroundColor: getEventColor(item.eventType) }}>
                                    <i className={`pi ${getEventIcon(item.eventType)}`} />
                                </div>
                            )}
                            content={renderTimelineContent}
                        />
                    ) : (
                        <div className={styles.emptyState}>
                            <i className="pi pi-inbox" />
                            <p>No timeline events yet</p>
                        </div>
                    )}
                </Card>

                {/* Stops Section */}
                <Card className={styles.stopsSection}>
                    <h2 className={styles.sectionTitle}>
                        <i className="pi pi-map-marker" />
                        Delivery Stops
                    </h2>

                    {stops && stops.length > 0 ? (
                        <div className={styles.stopsList}>
                            {stops.map((stop) => {
                                const badgeConfig = getStopStatusBadge(stop.status);
                                const hasProof = stop.deliveryProof && (stop.status === "Delivered" || stop.status === "Incomplete");
                                const isPartialDelivery = stop.deliveryProof?.isPartialDelivery;

                                return (
                                    <div key={stop._id} className={styles.stopCard}>
                                        <div className={styles.stopHeader}>
                                            <div className={styles.stopNumber}>#{stop.stopNumber}</div>
                                            <div className={styles.stopBadges}>
                                                <Badge value={stop.status} severity={badgeConfig.severity} icon={`pi ${badgeConfig.icon}`} />
                                                {isPartialDelivery && <Badge value="Partial" severity="warning" className={styles.partialBadge} />}
                                            </div>
                                        </div>

                                        <div className={styles.stopBody}>
                                            <h4>{stop.clientName}</h4>
                                            <div className={styles.stopInfo}>
                                                <div className={styles.infoItem}>
                                                    <i className="pi pi-box" />
                                                    <span>{stop.packageCount || 0} packages</span>
                                                </div>
                                                {stop.estimatedDistance > 0 && (
                                                    <div className={styles.infoItem}>
                                                        <i className="pi pi-compass" />
                                                        <span>{stop.estimatedDistance.toFixed(1)} km</span>
                                                    </div>
                                                )}
                                            </div>

                                            {/* Collapsible Packages List */}
                                            {stop.packages && stop.packages.length > 0 && (
                                                <Accordion className={styles.packagesAccordion}>
                                                    <AccordionTab
                                                        header={
                                                            <div className={styles.accordionHeader}>
                                                                <i className="pi pi-box" />
                                                                <span>View Packages ({stop.packages.length})</span>
                                                            </div>
                                                        }
                                                    >
                                                        <div className={styles.packagesList}>
                                                            {stop.packages.map((pkg, index) => {
                                                                const pkgBadge = getPackageStatusBadge(pkg.stopPackageStatus || pkg.packageStatus);
                                                                return (
                                                                    <div key={index} className={styles.packageItem}>
                                                                        <div className={styles.packageHeader}>
                                                                            <span className={styles.packageWaybill}>
                                                                                <i className="pi pi-tag" />
                                                                                {pkg.Waybill || "N/A"}
                                                                            </span>
                                                                            <Badge value={pkgBadge.label} severity={pkgBadge.severity} icon={`pi ${pkgBadge.icon}`} className={styles.packageBadge} />
                                                                        </div>
                                                                        <div className={styles.packageDetails}>
                                                                            {pkg.description && <p className={styles.packageDesc}>{pkg.description}</p>}
                                                                            <div className={styles.packageMeta}>
                                                                                {pkg.weight?.number && (
                                                                                    <span>
                                                                                        <i className="pi pi-inbox" />
                                                                                        {pkg.weight.number} {pkg.weight.unit || "kg"}
                                                                                    </span>
                                                                                )}
                                                                                {pkg.trackingNumber && (
                                                                                    <span>
                                                                                        <i className="pi pi-compass" />
                                                                                        {pkg.trackingNumber}
                                                                                    </span>
                                                                                )}
                                                                                {pkg.consigneeName && (
                                                                                    <span>
                                                                                        <i className="pi pi-user" />
                                                                                        {pkg.consigneeName}
                                                                                    </span>
                                                                                )}
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                );
                                                            })}
                                                        </div>
                                                    </AccordionTab>
                                                </Accordion>
                                            )}

                                            {stop.status === "Incomplete" && stop.incompleteReason && (
                                                <div className={styles.incompleteReason}>
                                                    <i className="pi pi-exclamation-circle" />
                                                    <span>{stop.incompleteReason}</span>
                                                </div>
                                            )}

                                            {hasProof && <Button label={isPartialDelivery ? "View Partial Delivery Proof" : "View Proof"} icon="pi pi-eye" outlined size="small" className={styles.proofButton} onClick={() => handleViewProof(stop)} />}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    ) : (
                        <div className={styles.emptyState}>
                            <i className="pi pi-inbox" />
                            <p>No stops found</p>
                        </div>
                    )}
                </Card>

                {/* Package Summary Section */}
                <Card className={styles.packageSummarySection}>
                    <h2 className={styles.sectionTitle}>
                        <i className="pi pi-box" />
                        Package Summary
                    </h2>

                    {(() => {
                        const { delivered, notDelivered } = getPackageSummary();
                        const totalPackages = delivered.length + notDelivered.length;

                        return (
                            <>
                                <div className={styles.packageSummaryStats}>
                                    <div className={styles.summaryStatCard}>
                                        <div className={styles.statIcon} style={{ background: "rgba(16, 185, 129, 0.1)" }}>
                                            <i className="pi pi-check-circle" style={{ color: "#10b981" }} />
                                        </div>
                                        <div className={styles.statContent}>
                                            <h3>{delivered.length}</h3>
                                            <p>Delivered</p>
                                            <span className={styles.statPercentage}>{totalPackages > 0 ? Math.round((delivered.length / totalPackages) * 100) : 0}%</span>
                                        </div>
                                    </div>

                                    <div className={styles.summaryStatCard}>
                                        <div className={styles.statIcon} style={{ background: "rgba(239, 68, 68, 0.1)" }}>
                                            <i className="pi pi-times-circle" style={{ color: "#ef4444" }} />
                                        </div>
                                        <div className={styles.statContent}>
                                            <h3>{notDelivered.length}</h3>
                                            <p>Not Delivered</p>
                                            <span className={styles.statPercentage}>{totalPackages > 0 ? Math.round((notDelivered.length / totalPackages) * 100) : 0}%</span>
                                        </div>
                                    </div>
                                </div>

                                <div className={styles.packageSummaryGrid}>
                                    {/* Delivered Packages */}
                                    <div className={styles.packageColumn}>
                                        <div className={styles.columnHeader} style={{ background: "linear-gradient(135deg, #10b981, #059669)" }}>
                                            <i className="pi pi-check-circle" />
                                            <h3>Delivered Packages ({delivered.length})</h3>
                                        </div>
                                        <div className={styles.packageListContainer}>
                                            {delivered.length > 0 ? (
                                                delivered.map((pkg, index) => (
                                                    <div key={index} className={styles.packageSummaryItem}>
                                                        <div className={styles.packageSummaryHeader}>
                                                            <span className={styles.packageStopNumber}>#{pkg.stopNumber}</span>
                                                            <span className={styles.packageWaybillSummary}>{pkg.Waybill || "N/A"}</span>
                                                            <Badge value="Delivered" severity="success" icon="pi pi-check" className={styles.packageStatusBadge} />
                                                        </div>
                                                        <div className={styles.packageSummaryDetails}>
                                                            <div className={styles.packageStopInfo}>
                                                                <i className="pi pi-map-marker" />
                                                                <span>{pkg.stopName}</span>
                                                            </div>
                                                            {pkg.description && <p className={styles.packageDescSummary}>{pkg.description}</p>}
                                                            <div className={styles.packageMetaSummary}>
                                                                {pkg.weight?.number && (
                                                                    <span>
                                                                        <i className="pi pi-inbox" />
                                                                        {pkg.weight.number} {pkg.weight.unit || "kg"}
                                                                    </span>
                                                                )}
                                                                {pkg.trackingNumber && (
                                                                    <span>
                                                                        <i className="pi pi-hashtag" />
                                                                        {pkg.trackingNumber}
                                                                    </span>
                                                                )}
                                                                {pkg.consigneeName && (
                                                                    <span>
                                                                        <i className="pi pi-user" />
                                                                        {pkg.consigneeName}
                                                                    </span>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </div>
                                                ))
                                            ) : (
                                                <div className={styles.emptyPackageList}>
                                                    <i className="pi pi-inbox" />
                                                    <p>No delivered packages</p>
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    {/* Not Delivered Packages */}
                                    <div className={styles.packageColumn}>
                                        <div className={styles.columnHeader} style={{ background: "linear-gradient(135deg, #ef4444, #dc2626)" }}>
                                            <i className="pi pi-times-circle" />
                                            <h3>Not Delivered Packages ({notDelivered.length})</h3>
                                        </div>
                                        <div className={styles.packageListContainer}>
                                            {notDelivered.length > 0 ? (
                                                notDelivered.map((pkg, index) => (
                                                    <div key={index} className={styles.packageSummaryItem}>
                                                        <div className={styles.packageSummaryHeader}>
                                                            <span className={styles.packageStopNumber}>#{pkg.stopNumber}</span>
                                                            <span className={styles.packageWaybillSummary}>{pkg.Waybill || "N/A"}</span>
                                                            <Badge
                                                                value={pkg.stopStatus === "Incomplete" ? "Incomplete" : "Pending"}
                                                                severity={pkg.stopStatus === "Incomplete" ? "warning" : "info"}
                                                                icon={pkg.stopStatus === "Incomplete" ? "pi pi-exclamation-triangle" : "pi pi-clock"}
                                                                className={styles.packageStatusBadge}
                                                            />
                                                        </div>
                                                        <div className={styles.packageSummaryDetails}>
                                                            <div className={styles.packageStopInfo}>
                                                                <i className="pi pi-map-marker" />
                                                                <span>{pkg.stopName}</span>
                                                            </div>
                                                            {pkg.description && <p className={styles.packageDescSummary}>{pkg.description}</p>}
                                                            <div className={styles.packageMetaSummary}>
                                                                {pkg.weight?.number && (
                                                                    <span>
                                                                        <i className="pi pi-inbox" />
                                                                        {pkg.weight.number} {pkg.weight.unit || "kg"}
                                                                    </span>
                                                                )}
                                                                {pkg.trackingNumber && (
                                                                    <span>
                                                                        <i className="pi pi-hashtag" />
                                                                        {pkg.trackingNumber}
                                                                    </span>
                                                                )}
                                                                {pkg.consigneeName && (
                                                                    <span>
                                                                        <i className="pi pi-user" />
                                                                        {pkg.consigneeName}
                                                                    </span>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </div>
                                                ))
                                            ) : (
                                                <div className={styles.emptyPackageList}>
                                                    <i className="pi pi-inbox" />
                                                    <p>No undelivered packages</p>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </>
                        );
                    })()}
                </Card>

                {/* Driver Location Trail Section */}
                <Card className={styles.locationTrailSection}>
                    <div className={styles.sectionTitleWithAction}>
                        <h2 className={styles.sectionTitle}>
                            <i className="pi pi-map" />
                            Driver Location Trail
                            {locationTrail.length > 0 && <Badge value={`${locationTrail.length} points`} severity="info" className={styles.trailBadge} />}
                        </h2>
                        {isMapLoaded && locationTrail.length > 0 && <Button label="Fit All" icon="pi pi-arrows-alt" size="small" outlined onClick={fitMapBounds} className={styles.fitButton} />}
                    </div>

                    {loadingLocations ? (
                        <div className={styles.loadingMap}>
                            <i className="pi pi-spin pi-spinner" style={{ fontSize: "2rem", color: "#6366f1" }} />
                            <p>Loading location trail...</p>
                        </div>
                    ) : locationTrail.length > 0 ? (
                        <div className={styles.mapWrapper}>
                            {/* Map Statistics */}
                            <div className={styles.mapStats}>
                                <div className={styles.statItem}>
                                    <i className="pi pi-map-marker" />
                                    <div>
                                        <span className={styles.statValue}>{locationTrail.length}</span>
                                        <span className={styles.statLabel}>Trail Points</span>
                                    </div>
                                </div>
                                <div className={styles.statItem}>
                                    <i className="pi pi-calendar" />
                                    <div>
                                        <span className={styles.statValue}>{formatDate(locationTrail[0]?.timestamp)}</span>
                                        <span className={styles.statLabel}>First Location</span>
                                    </div>
                                </div>
                                <div className={styles.statItem}>
                                    <i className="pi pi-clock" />
                                    <div>
                                        <span className={styles.statValue}>{formatDate(locationTrail[locationTrail.length - 1]?.timestamp)}</span>
                                        <span className={styles.statLabel}>Last Location</span>
                                    </div>
                                </div>
                            </div>

                            {/* Map Container */}
                            <div className={styles.mapContainer}>
                                <LoadScript googleMapsApiKey={process.env.REACT_APP_GOOGLE_MAPS_API_KEY}>
                                    <GoogleMap
                                        mapContainerStyle={{ width: "100%", height: "100%" }}
                                        center={mapCenter}
                                        zoom={13}
                                        options={{
                                            disableDefaultUI: false,
                                            zoomControl: true,
                                            streetViewControl: false,
                                            mapTypeControl: true,
                                            fullscreenControl: true,
                                        }}
                                        onLoad={(map) => {
                                            mapRef.current = map;
                                            setIsMapLoaded(true);
                                            console.log("🗺️ Map loaded successfully");

                                            // Fit bounds after map loads
                                            setTimeout(() => {
                                                fitMapBounds();
                                            }, 500);
                                        }}
                                    >
                                        {isMapLoaded && (
                                            <>
                                                {/* Location Trail Polyline */}
                                                {locationTrail.length > 1 && (
                                                    <Polyline
                                                        path={locationTrail}
                                                        options={{
                                                            strokeColor: "#6366f1",
                                                            strokeOpacity: 0.8,
                                                            strokeWeight: 4,
                                                            geodesic: true,
                                                        }}
                                                    />
                                                )}

                                                {/* Trail Markers (faded) */}
                                                {locationTrail.slice(0, -1).map((location, index) => (
                                                    <Marker
                                                        key={`trail-${index}`}
                                                        position={{ lat: location.lat, lng: location.lng }}
                                                        icon={{
                                                            path: window.google.maps.SymbolPath.CIRCLE,
                                                            scale: 4,
                                                            fillColor: "#6366f1",
                                                            fillOpacity: 0.3,
                                                            strokeColor: "#4f46e5",
                                                            strokeWeight: 1,
                                                        }}
                                                        title={`${formatDate(location.timestamp)}`}
                                                    />
                                                ))}

                                                {/* Start Marker */}
                                                {locationTrail.length > 0 && (
                                                    <Marker
                                                        position={{ lat: locationTrail[0].lat, lng: locationTrail[0].lng }}
                                                        icon={{
                                                            url: "http://maps.google.com/mapfiles/ms/icons/green-dot.png",
                                                        }}
                                                        title={`Start: ${formatDate(locationTrail[0].timestamp)}`}
                                                        label={{
                                                            text: "START",
                                                            color: "white",
                                                            fontSize: "10px",
                                                            fontWeight: "bold",
                                                        }}
                                                    />
                                                )}

                                                {/* Latest Location Marker (animated) */}
                                                {locationTrail.length > 0 && (
                                                    <Marker
                                                        position={{ lat: locationTrail[locationTrail.length - 1].lat, lng: locationTrail[locationTrail.length - 1].lng }}
                                                        icon={{
                                                            url:
                                                                "data:image/svg+xml;charset=UTF-8," +
                                                                encodeURIComponent(`
                                                                    <svg width="40" height="40" viewBox="0 0 40 40" xmlns="http://www.w3.org/2000/svg">
                                                                        <circle cx="20" cy="20" r="18" fill="#6366f1" stroke="#ffffff" stroke-width="3"/>
                                                                        <circle cx="20" cy="20" r="8" fill="#ffffff"/>
                                                                    </svg>
                                                                `),
                                                            scaledSize: new window.google.maps.Size(40, 40),
                                                            anchor: new window.google.maps.Point(20, 20),
                                                        }}
                                                        title={`Latest: ${formatDate(locationTrail[locationTrail.length - 1].timestamp)}`}
                                                        animation={window.google.maps.Animation.BOUNCE}
                                                    />
                                                )}

                                                {/* Delivery Stops */}
                                                {stops &&
                                                    stops.map((stop, index) => (
                                                        <Marker
                                                            key={stop._id}
                                                            position={{ lat: stop.latitude, lng: stop.longitude }}
                                                            label={{
                                                                text: `${index + 1}`,
                                                                color: "#ffffff",
                                                                fontWeight: "bold",
                                                            }}
                                                            icon={{
                                                                url:
                                                                    stop.status === "Delivered"
                                                                        ? "http://maps.google.com/mapfiles/ms/icons/green-dot.png"
                                                                        : stop.status === "Incomplete"
                                                                          ? "http://maps.google.com/mapfiles/ms/icons/yellow-dot.png"
                                                                          : "http://maps.google.com/mapfiles/ms/icons/red-dot.png",
                                                            }}
                                                            title={`Stop ${index + 1}: ${stop.clientName} (${stop.status})`}
                                                        />
                                                    ))}
                                            </>
                                        )}
                                    </GoogleMap>
                                </LoadScript>
                            </div>

                            {/* Timeline List */}
                            <div className={styles.locationTimeline}>
                                <h3>
                                    <i className="pi pi-list" />
                                    Location History
                                </h3>
                                <div className={styles.timelineList}>
                                    {locationTrail
                                        .slice()
                                        .reverse()
                                        .slice(0, 10)
                                        .map((location, index) => (
                                            <div key={index} className={styles.timelineItem}>
                                                <div className={styles.timelineDot} />
                                                <div className={styles.timelineContent}>
                                                    <div className={styles.timelineHeader}>
                                                        <span className={styles.timelineTime}>{formatDate(location.timestamp)}</span>
                                                        {location.accuracy && <span className={styles.timelineAccuracy}>±{location.accuracy.toFixed(1)}m</span>}
                                                    </div>
                                                    <div className={styles.timelineCoords}>
                                                        <span>
                                                            {location.lat.toFixed(6)}, {location.lng.toFixed(6)}
                                                        </span>
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    {locationTrail.length > 10 && (
                                        <div className={styles.moreLocations}>
                                            <i className="pi pi-ellipsis-h" />
                                            <span>+{locationTrail.length - 10} more locations</span>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    ) : (
                        <div className={styles.emptyState}>
                            <i className="pi pi-map" />
                            <p>No Location Trail</p>
                            <span>Location tracking data not available for this delivery</span>
                        </div>
                    )}
                </Card>
            </div>

            {/* Proof Dialog */}
            <Dialog header={selectedProof?.deliveryProof?.isPartialDelivery ? "Partial Delivery Proof" : "Delivery Proof"} visible={showProofDialog} onHide={() => setShowProofDialog(false)} className={styles.proofDialog} breakpoints={{ "960px": "90vw" }}>
                {selectedProof && selectedProof.deliveryProof && (
                    <div className={styles.proofContent}>
                        {selectedProof.deliveryProof.isPartialDelivery && (
                            <div className={styles.partialDeliveryBanner}>
                                <i className="pi pi-info-circle" />
                                <div>
                                    <strong>Partial Delivery</strong>
                                    <p>Some packages were delivered, others marked incomplete</p>
                                </div>
                            </div>
                        )}

                        <div className={styles.proofSection}>
                            <h4>Delivery Photo</h4>
                            <img src={selectedProof.deliveryProof.photoUrl} alt="Delivery proof" className={styles.proofImage} />
                        </div>

                        <div className={styles.proofSection}>
                            <h4>Customer Signature</h4>
                            <img src={selectedProof.deliveryProof.signatureUrl} alt="Signature" className={styles.signatureImage} />
                        </div>

                        <div className={styles.proofMeta}>
                            <i className="pi pi-clock" />
                            <span>Submitted: {formatDate(selectedProof.deliveryProof.submittedAt)}</span>
                        </div>
                    </div>
                )}
            </Dialog>
        </div>
    );
};

export default DeliveryDetails;
